import React from 'react'
import {useParams} from 'react-router-dom';
import {ErrorBoundary} from 'react-error-boundary';
import {graphql, useLazyLoadQuery, usePaginationFragment} from 'react-relay/hooks';
import {Helmet} from 'react-helmet-async';
import SeoMetaTags from '../../../components/seo-meta-tags';
import Marked from '../../../components/Marked';
import ErrorFallback from '../../../components/ErrorFallback';
import SuspenseImageDynamicSize from '../../../components/SuspenseImageDynamicSize';
import FacebookIcon from '../../../assets/icons0/socials/facebook-icon.svg';
import InstagramIcon from '../../../assets/icons0/socials/instagram-icon.svg';
import TelegramIcon from '../../../assets/icons0/socials/telegram-icon.svg';
import {FetchKeyStateContext} from '../../../components/FetchKey';
import ServiceList from './service-list';
import NotFoundPage from '../../../components/not-found';
import getTagValue from '../../../utilities/get-tag-value';
import {format, isAfter, isBefore} from 'date-fns';
import CalendarIcon from '../../../assets/icons0/calendar.svg';

const CHARORCHESTRA_DEFAULT = {
    merchantId: '66154a6381a8c366b1d6544a',
    name: 'Charorchestra',
    theme: 'dark',
    posterUrl: 'ticket.demo.impulse.ottry.com/merchants/6610feb481a8c366b1d64e9a/services/661104b581a8c366b1d64eab/0e346eda-1ea1-494c-bb24-5ecabfd90bb5.png',
    details:
`
Char orchestra - колектив молодих музикантів,що складається з солістів провідних національних колективів України. В репертуарі Чару твори від музики епохи бароко,української класики, танго до сучасних рок-хітів та голівудських саундтреків.

«Всім відомі композиції у виконанні Сhar починають звучати по-новому,вони постають у зовсім інших фарбах і тембри академічних інструментів відкривають її нові грані. Для нас важливо розширити ці горизонти, показати наскільки оркестр глибокий та різноплановий інструмент»
`,
    social: {
        facebook: '',
        instagram: 'https://www.instagram.com/charorchestra',
        telegram: ''
    }   
};

const getSpaceMeta = ({name, eventsStart = '', eventsEnd}) => {
    let date = eventsStart;
    if (eventsEnd && eventsStart !== eventsEnd) {
        date = `${date} — ${eventsEnd} `;
    }
    return {
        title: `${name}: Купити квитки на події ${date}| Ottry`,
        description: `${name}: Купити квитки на події ${date}✅ Інформація, розклад подій на ottry.com`
    }
};

const SPACES = {
    'charorchestra': {
        ...CHARORCHESTRA_DEFAULT,
        filterBy: 'slug',
    },
    'charorchestra-valentines-day': {
        ...CHARORCHESTRA_DEFAULT,
        filterBy: 'merchantTarget',
        targetDate: '14-02-2026',
    },
    'charorchestra-filter-by-target': {
        ...CHARORCHESTRA_DEFAULT,
        filterBy: 'merchantTarget',
    },
    'dovzhenkofilm': {
        filterBy: 'slug',
        merchantId: '68e3774afe2f95685c3aedc4',
        name: 'Кіностудія Довженка',
        theme: 'light',
        posterUrl: 'ticket.demo.impulse.ottry.com/merchants/68e3774afe2f95685c3aedc4/services/690f8b9cb946526fb59e8834/0a22972a-f68c-43ea-b409-f8b73fcef17b.jpg',
        details:
`
Екскурсії Національною кіностудією імені Олександра Довженка пропонують відвідувачам унікальну можливість зазирнути за лаштунки однієї з найвідоміших кіноінституцій України. Заснована в 1927 році, студія відіграла центральну роль у розвитку українського кінематографа, а її екскурсії дозволяють гостям зануритися в майже столітню історію кіно.

Під час екскурсії відвідувачі можуть оглянути автентичні знімальні майданчики, дізнатися про традиційні та сучасні техніки кіновиробництва та дізнатися, як створювалися легендарні українські фільми. Гості також мають можливість оглянути історичне обладнання, реквізит та костюми, що зберігаються в колекції студії.

Екскурсії Кіностудії імені Довженка розроблені для кіноманів, студентів, туристів та всіх, хто цікавиться культурною спадщиною українського кінематографа. Це надихаючий та пізнавальний досвід, який поєднує відвідувачів зі спадщиною Олександра Довженка та історією національного кіномистецтва, що триває.
`,
        social: {
            facebook: 'https://www.facebook.com/dovzhenkofilmstudios',
            instagram: 'https://www.instagram.com/dovzhenkofilmstudios/',
            telegram: 'https://t.me/gidDov'
        }
    },
    'man1k-wild-heart-tour': {
        filterBy: 'spaceName',
        merchantId: '6a1d88be02fee7aa88a205bc',
        name: 'MAN1K - Wild Heart Tour',
        spaceName: 'man1k-wild-heart-tour',
        theme: 'dark',
        posterUrl: 'ticket.demo.impulse.ottry.com/merchants/6a1d88be02fee7aa88a205bc/services/6a2ab450e40e2faf3f557604/3842c813-a86f-48b5-b02a-0b97efcf6636.jpg',
        details:
`
Є речі, які трапляються один раз. Не тому, що так вирішили організатори, а тому, що темрява з якої вони народжуються, більше не збереться в одному місці ніколи. Цього літа крізь вісім міст пройде щось, що не відпустить вже ніколи. Точка відліку - до і після. І низький, тягучий звук, що б'є в груди, саме туди, у чому ти ніколи нікому не зізнавався навіть собі.

З Японії повертається спадкоємець формації #УВХР. MAN1K - артист, чия музика будувалась не для сцени, а для тих, хто давно перестав шукати себе у світлі. Для тих, хто знає: справжнє живе лише там, де незручно.

На кожному шоу - лімітований мерч: футболки, афіші та ексклюзивні CD із демо-записами. Без перевидань. Без стрімінгів. Після останньої ночі зникне разом із нею - як зникає все, що було справжнім. Доторкнувшись - вже не відпустить вічність.
`,
        social: {
            instagram: 'https://www.instagram.com/http.man1k/'
        }
    }
};

const Skeleton = () => {
    return (
        <div className='skeleton'>
            <div className='skeleton__title'>&nbsp;</div>
            <ul className='skeleton__paragraph'>
                <li>&nbsp;</li>
                <li>&nbsp;</li>
                <li>&nbsp;</li>
            </ul>
        </div>
    );
}

const SpaceDescription = ({viewer, actionBoxRef, spaceDescription}) => {
    const {space} = useParams();
    const {data, loadNext, hasNext, isLoadingNext} = usePaginationFragment(
        graphql`
            fragment descriptionSpaceServicesByViewerQuery_viewer on Viewer @refetchable(queryName: "descriptionSpaceServicesQuery") {
                targetServices(first: $first, after: $after, merchantId: $merchantId) @connection(key: "descriptionSpace_targetServices") {
                    edges {
                        node {
                            id
                            slug
                            name
                            nameUk
                            warp
                            target
                            description
                            labels
                            merchant {
                                id
                            }
                            endpoints {
                                id
                                price
                                labels
                            }
                        }
                    }
                }
            }
        `,
        viewer
    );
    React.useEffect(() => {
        let cancelled = false;
        const loadAll = async () => {
            if (isLoadingNext) return;
            try {
                while (hasNext && !cancelled) {
                    await new Promise((resolve, reject) => {
                        loadNext(8, {
                            onComplete: (err) => {
                                if (err) {
                                    reject(err);
                                } else {
                                    resolve();
                                }
                            }
                        })
                    })
                }
            } finally {

            }
        }
        loadAll();
        return () => {
            cancelled = true;
        }
    }, [hasNext, isLoadingNext]);
    const {posterUrl, theme, filterBy, name, details, merchantId, targetDate, spaceName} = spaceDescription;
    const {facebook, instagram, telegram} = spaceDescription.social;
    
    const spaceServiceList = React.useMemo(() => {
        if (filterBy === 'slug') {
            return data.targetServices.edges.map(e => e.node).filter(item => {
                return item.merchant.id === merchantId;
            });
        } else if (filterBy === 'merchantId') {
            return data.targetServices.edges.map(e => e.node).filter(item => {
                return item.merchant.id === space;
            });
        } else if (filterBy === 'spaceName') {
            return data.targetServices.edges.map(e => e.node).filter(item => {
                return item.labels?.spaceName === spaceName;
            });
        } else if (filterBy === 'merchantTarget') {
            return data.targetServices.edges.map(e => e.node).filter(item => {
                return item.merchant.id === merchantId && format(new Date(item.target), 'dd-MM-yyyy') === targetDate;
            });
        }
        return [];
    }, [data, filterBy, space]);
    const {eventsStartDate, eventsEndDate} = spaceServiceList.reduce((val, service) => {
        if (service.target) {
            const d = new Date(service.target);
            if (val.eventsStartDate) {
                val.eventsStartDate = isBefore(d, val.eventsStartDate) ? d : val.eventsStartDate;
            } else {
                val.eventsStartDate = d;
            }
            if (val.eventsEndDate) {
                val.eventsEndDate = isAfter(d, val.eventsEndDate) ? d : val.eventsEndDate;
            } else {
                val.eventsEndDate = d;
            }
        }
        return val;
    }, {eventsStartDate: null, eventsEndDate: null});
    const eventsStart = eventsStartDate ? format(eventsStartDate, 'dd-MM-yyyy') : null;
    const eventsEnd = eventsEndDate ? format(eventsEndDate, 'dd-MM-yyyy') : null;
    const {title, description} = getSpaceMeta({name, eventsStart, eventsEnd});
    return (
        <>
        <SeoMetaTags {...{meta: {title, description}}}/>
        <Helmet>
            {!!posterUrl && <meta property='og:image' content={`https://${posterUrl}`}/>}
            <meta property="og:type" content="article"/>
        </Helmet>
        <div>
            <div className={`view theme theme--${theme}`}>
                {posterUrl &&
                    <div className='space__poster-img-container max-height-35rem mw768-max-height-37dot5rem margin-0-auto max-width-100vw mw768-max-width-76rem display-flex justify-content-center'>
                        <ErrorBoundary {...{FallbackComponent: ErrorFallback}}>
                            <SuspenseImageDynamicSize
                                className='space__poster-img'
                                src={`https://${posterUrl}`}
                            />
                        </ErrorBoundary>
                    </div>
                }
                <div className='block'>
                    <div className='max-width-50rem margin-0-auto'>
                        <h1 className='space__title display-sm semibold padding-top-2rem mw768-padding-top-4rem'>
                            {name}
                        </h1>
                        {eventsStart &&
                            <div className='padding-top-0dot5rem space__target display-flex'>
                                <CalendarIcon className='display-block height-1dot25rem width-1dot25rem'/>
                                <p className='text-md medium padding-left-0dot5rem'>{eventsStart}</p>
                                {eventsEnd && eventsStart !== eventsEnd &&
                                    <span className='text-md medium padding-left-0dot25rem'>{` — ${eventsEnd}`}</span>
                                }
                            </div>
                        }
                        <div className='padding-top-1dot5rem'>
                            <Marked {...{content: details}}/>
                        </div>
                        {(facebook || instagram || telegram) &&
                            <div className='padding-top-1dot25rem mw768-padding-top-3rem display-flex gap-1dot25rem'>
                                {facebook &&
                                    <a href={facebook} target='_blank' rel='noopener noreferrer'>
                                        <FacebookIcon className='space__social-icon display-block height-1dot5rem mw768-height-2rem width-1dot5rem mw768-width-2rem'/>
                                    </a>
                                }
                                {instagram &&
                                    <a href={instagram} target='_blank' rel='noopener noreferrer'>
                                        <InstagramIcon className='space__social-icon display-block height-1dot5rem mw768-height-2rem width-1dot5rem mw768-width-2rem'/>
                                    </a>
                                }
                                {telegram &&
                                    <a href={telegram} target='_blank' rel='noopener noreferrer'>
                                        <TelegramIcon className='space__social-icon display-block height-1dot5rem mw768-height-2rem width-1dot5rem mw768-width-2rem'/>
                                    </a>
                                }
                            </div>
                        }
                        {(hasNext || isLoadingNext) ?
                            <div className='mw768-margin-top-1rem padding-top-2rem padding-bottom-2rem'>
                                <Skeleton/>
                            </div>
                            :
                            <div className='mw768-margin-top-1rem padding-top-2rem padding-bottom-2rem' ref={actionBoxRef}>
                                <ServiceList {...{list: spaceServiceList, theme}}/>
                            </div>
                        }
                        
                    </div>
                </div>
            </div>
        </div>
        </>
    )
};

const SpaceDescriptionWrapper = ({actionBoxRef}) => {
    const {space, filter} = useParams();
    const spaceName = space === 'charorchestra' && filter ? 'charorchestra-filter-by-target' : space;
    const spaceDescription = SPACES[spaceName] ? {...SPACES[spaceName]} : null;
    if (!spaceDescription) {
        return (
            <div className='view theme theme--light'>
                <div className='block'>
                    <div className='margin-0-auto'>
                        <NotFoundPage/>
                    </div>
                </div>
            </div>
        )
    };
    if (space === 'charorchestra' && filter) {
        spaceDescription.targetDate = filter;
    }
    const fetchKeyState = React.useContext(FetchKeyStateContext);
    const data = useLazyLoadQuery(
        graphql`
            query descriptionSpaceServicesByViewerQuery($first: Int!, $after: String, $merchantId: String) {
                viewer {
                    ...descriptionSpaceServicesByViewerQuery_viewer
                }
            }
        `,
        {first: 8, after: 0, merchantId: spaceDescription.merchantId},
        {fetchKey: fetchKeyState}
    );
    return (
        <SpaceDescription {...{actionBoxRef, viewer: data.viewer, spaceDescription}} />
    )
}

export default React.memo(SpaceDescriptionWrapper);